import { connect, iAmReady, syncPathname } from '@tiendanube/nexo';
import { Box, Spinner } from '@nimbus-ds/components';
import { useEffect, useState } from 'react';
import { Shell } from './components/Shell.js';
import { RouteSkeleton } from './components/skeletons.js';
import { ErrorState } from './components/states.js';
import nexo from './nexoClient.js';
import { Analytics } from './pages/Analytics.js';
import { Dashboard } from './pages/Dashboard.js';
import { FormDesigner } from './pages/FormDesigner.js';
import { QuantityOffers } from './pages/QuantityOffers.js';
import { Settings } from './pages/Settings.js';
import { ShippingRates } from './pages/ShippingRates.js';
import { TrackingPixels } from './pages/TrackingPixels.js';
import { ROUTE_TO_PATH, pathToRoute, type Route } from './routes.js';
import { AppStateProvider, useAppState } from './state/AppState.js';
import { NavigationGuardProvider, useNavigationGuard } from './state/NavigationGuard.js';

/**
 * Performs the Nexo handshake (connect -> iAmReady) so the admin starts talking to the
 * Nuvemshop admin shell. Until connected we show a spinner. Route changes are mirrored to
 * the admin via syncPathname so the iframe URL stays in sync.
 */
function useNexoConnection(): boolean {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let active = true;
    // Race the handshake against a timeout: inside the iframe it resolves quickly;
    // standalone (dev, no parent admin) connect() would hang forever, so we proceed
    // after the timeout and let the axios dev-token fallback handle auth.
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 1600));
    void Promise.race([connect(nexo), timeout])
      .then(() => {
        if (!active) return;
        iAmReady(nexo);
        setConnected(true);
      })
      .catch(() => {
        if (active) setConnected(true);
      });
    return () => {
      active = false;
    };
  }, []);
  return connected;
}

function Router(): JSX.Element {
  const { status, reload } = useAppState();
  const guard = useNavigationGuard();
  const [route, setRoute] = useState<Route>(() => pathToRoute(window.location.pathname));

  // The app's ONE route change. The sidebar and the dashboard's own links both call it, so
  // guarding it here is what stops either of them walking away from unsaved edits — no page
  // has to remember to ask. Re-selecting the current route is not a navigation and must not
  // prompt: the sidebar's accordion parents fire it just to open themselves.
  const navigate = (next: Route): void => {
    if (next === route) return;
    guard.request(() => {
      setRoute(next);
      syncPathname(nexo, ROUTE_TO_PATH[next]);
    });
  };

  // Shell-level states, so every page inherits them. A page only adds its own where its data loads
  // separately from the initial fetch (Analytics, QuantityOffers, TrackingPixels).
  //
  // 🚨 Both of them render INSIDE the Shell, and that is the point. They used to replace it — a
  // bare `Box` of grey bars, and then, when the fetch landed, the sidebar, the header and the
  // language switcher all appeared at once and shoved the content sideways. The heaviest piece of
  // layout in the app needs no data at all, so it can be on screen from the first frame; the
  // skeleton then only has to stand in for the page body. It also means the merchant can navigate
  // while the fetch is in flight instead of staring at a locked screen.
  //
  // The route is what picks the skeleton's shape (`RouteSkeleton`): a deep link to /analytics is
  // about to render eleven cards, not the dashboard's checklist.
  if (status === 'loading') {
    return (
      <Shell route={route} onNavigate={navigate}>
        <RouteSkeleton route={route} />
      </Shell>
    );
  }
  if (status === 'error') {
    // 🚨 This used to be a bare `<Text>{t('common.saveError')}</Text>` — the SAVE error string on a
    // LOAD failure, with no way out. The merchant's only recourse was reloading the iframe, which
    // inside the Nuvemshop admin is not an obvious action. Homologation's checklist asks for an
    // error template with a retry, and this is the one every page falls back to.
    return (
      <Shell route={route} onNavigate={navigate}>
        <Box padding="4">
          <ErrorState onRetry={reload} />
        </Box>
      </Shell>
    );
  }

  return (
    <Shell route={route} onNavigate={navigate}>
      {route === 'dashboard' && <Dashboard onNavigate={navigate} />}
      {route === 'analytics' && <Analytics />}
      {route === 'designer' && <FormDesigner />}
      {route === 'shipping-rates' && <ShippingRates />}
      {route === 'quantity-offers' && <QuantityOffers />}
      {route === 'tracking' && <TrackingPixels />}
      {route === 'settings' && <Settings />}
    </Shell>
  );
}

export function App(): JSX.Element {
  const connected = useNexoConnection();
  if (!connected) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Spinner size="large" />
      </Box>
    );
  }
  return (
    <AppStateProvider>
      <NavigationGuardProvider>
        <Router />
      </NavigationGuardProvider>
    </AppStateProvider>
  );
}
